import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ProjectTile = ({ project }) => {
  const {
    id,
    name,
    color,
    boxShadowColor = "#00ffff",
    description,
    githubLink,
    linksNote,
    link,
    clip,
    video,
    poster
  } = project;

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showPosterModal, setShowPosterModal] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showVideoModal || showPosterModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [showVideoModal, showPosterModal]);

  const handleCardClick = (e) => {
    // Only act if clicking on the card itself, not on specific links or buttons
    if (e.target.closest('a') || e.target.closest('button')) {
      return;
    }

    // Priority: link > video > poster > github
    if (link && link.trim() !== "") {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else if (video) {
      setShowVideoModal(true);
    } else if (poster) {
      setShowPosterModal(true);
    } else if (githubLink && githubLink.trim() !== "") {
      window.open(githubLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleVideoClick = (e) => {
    e.stopPropagation();
    setShowVideoModal(true);
  };

  const handlePosterClick = (e) => {
    e.stopPropagation();
    setShowPosterModal(true);
  };

  const handleCloseVideoModal = (e) => {
    e.stopPropagation();
    setShowVideoModal(false);
  };

  const handleClosePosterModal = (e) => {
    e.stopPropagation();
    setShowPosterModal(false);
  };

  return (
    <>
      <div className="project-tile" id={`project-tile-${id}`}>
        <div
          className="project-inner-tile"
          style={{
            backgroundColor: color,
            '--accent': boxShadowColor
          }}
          onClick={handleCardClick}
        >
          <div className="project-header">
            <span>{name}</span>
          </div>

          <div className="project-image-frame">
            {clip ? (
              <video
                className="project-image"
                autoPlay
                loop
                muted
                playsInline
              >
                <source src={`./videos/project-clips/project-clip-${id}.mp4`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                className="project-image"
                src={`./images/project-images/project-image-${id}.png`}
                alt={`${name} screenshot`}
              />
            )}
          </div>

          <div className="project-description">{description}</div>

          <div className="project-link-row">
            {githubLink && githubLink.trim() !== "" ? (
              <a
                href={githubLink}
                target="_blank"
                rel="noreferrer"
                aria-label="GitHub"
              >
                <i className="fa fa-github" aria-hidden="true" />
                <span className="sr-only">GitHub</span>
              </a>
            ) : (
              <span className="project-link-placeholder" aria-hidden="true" />
            )}

            {linksNote && linksNote.trim() !== "" ? (
              <p className="linksNote">{linksNote}</p>
            ) : (
              <span className="project-link-placeholder" aria-hidden="true" />
            )}

            {video ? (
              <button
                className="project-video-button"
                onClick={handleVideoClick}
                aria-label="Watch video"
              >
                <i className="fa fa-play-circle" aria-hidden="true" />
              </button>
            ) : link && link.trim() !== "" ? (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                aria-label="Website"
              >
                <i className="fa fa-globe" aria-hidden="true" />
                <span className="sr-only">Website</span>
              </a>
            ) : poster ? (
              <button
                className="project-video-button"
                onClick={handlePosterClick}
                aria-label="View poster"
              >
                <i className="fa fa-file-image-o" aria-hidden="true" />
              </button>
            ) : (
              <span className="project-link-placeholder" aria-hidden="true" />
            )}
          </div>
        </div>
      </div>

      {showVideoModal && video && createPortal(
        <div className="project-video-modal-overlay" onClick={handleCloseVideoModal}>
          <div className="project-video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="project-video-modal-close" onClick={handleCloseVideoModal} aria-label="Close">
              <i className="fa fa-times" aria-hidden="true" />
            </button>
            <video className="project-video-player" controls autoPlay>
              <source src={`./videos/project-videos/project-video-${id}.mp4`} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>,
        document.body
      )}

      {showPosterModal && poster && createPortal(
        <div className="project-poster-modal-overlay" onClick={handleClosePosterModal}>
          <div className="project-poster-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="project-poster-controls">
              <button className="project-poster-modal-close" onClick={handleClosePosterModal} aria-label="Close">
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
            <div className="project-poster-scroll-container">
              <img
                className="project-poster-image"
                src={`./images/project-posters/project-poster-${id}.png`}
                alt={`${name} poster`}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ProjectTile;
